import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiClock, FiDollarSign, FiTruck } = FiIcons;

const Services = () => {
  const services = [
    {
      title: 'Wash & Fold Service',
      description: 'Complete washing, drying, and folding service for your everyday clothes.',
      image: 'https://images.unsplash.com/photo-1582735689369-4fe89db7114c?w=600&h=400&fit=crop',
      features: [
        'Premium detergents used',
        'Fabric softener included',
        'Clothes sorted by color',
        'Professional folding',
        'Same day service available'
      ],
      pricing: 'Starting at $1.50 per pound',
      turnaround: '24-48 hours'
    },
    {
      title: 'Dry Cleaning',
      description: 'Expert dry cleaning for delicate fabrics, suits, dresses, and special garments.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop',
      features: [
        'Eco-friendly solvents',
        'Stain treatment included',
        'Professional pressing',
        'Protective garment bags',
        'Special fabric care'
      ],
      pricing: 'Starting at $8.99 per item',
      turnaround: '2-3 business days'
    },
    {
      title: 'Pickup & Delivery',
      description: 'Convenient door-to-door service for busy professionals and families.',
      image: 'https://images.unsplash.com/photo-1517677208171-0bc6725a3e60?w=600&h=400&fit=crop',
      features: [
        'Free within 5 miles',
        'Flexible scheduling',
        'SMS notifications',
        'Contactless service',
        'Same day pickup available'
      ],
      pricing: 'Free with $25+ orders',
      turnaround: 'Scheduled pickup times'
    },
    {
      title: 'Commercial Laundry',
      description: 'Bulk laundry services for restaurants, hotels, gyms, and other businesses.',
      image: 'https://images.unsplash.com/photo-1545558014-8692077e9b5c?w=600&h=400&fit=crop',
      features: [
        'Volume discounts',
        'Regular pickup schedule',
        'Commercial-grade equipment',
        'Quick turnaround',
        'Custom service plans'
      ],
      pricing: 'Custom pricing available',
      turnaround: 'Same day or next day'
    },
    {
      title: 'Alterations & Repairs',
      description: 'Professional tailoring and garment repair services.',
      image: 'https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=600&h=400&fit=crop',
      features: [
        'Hemming and resizing',
        'Zipper replacement',
        'Button replacement',
        'Tear repairs',
        'Custom fitting'
      ],
      pricing: 'Starting at $12.99',
      turnaround: '3-5 business days'
    },
    {
      title: 'Specialty Items',
      description: 'Special care for comforters, curtains, rugs, and other large items.',
      image: 'https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=600&h=400&fit=crop',
      features: [
        'Comforters and quilts',
        'Curtains and drapes',
        'Area rugs',
        'Sleeping bags',
        'Wedding dress preservation'
      ],
      pricing: 'Starting at $25.99',
      turnaround: '3-7 business days'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Our Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Complete laundry and dry cleaning solutions for all your needs. Professional care, 
            convenient service, and exceptional results every time.
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {services.map((service, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <img 
                src={service.image} 
                alt={service.title}
                className="w-full h-64 object-cover"
              />
              <div className="p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-3">{service.title}</h2>
                <p className="text-gray-600 mb-6">{service.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Features:</h3>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center space-x-2">
                          <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                          <span className="text-gray-600 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-blue-600" />
                        <div>
                          <span className="text-sm text-gray-500">Pricing:</span>
                          <p className="font-semibold text-gray-800">{service.pricing}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiClock} className="w-5 h-5 text-blue-600" />
                        <div>
                          <span className="text-sm text-gray-500">Turnaround:</span>
                          <p className="font-semibold text-gray-800">{service.turnaround}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <Link
                  to="/contact"
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors duration-200 text-center block font-semibold"
                >
                  Get Quote for This Service
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Info Section */}
      <div className="bg-blue-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <SafeIcon icon={FiTruck} className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Free Pickup & Delivery</h3>
              <p>Within 5 miles radius for orders over $25</p>
            </div>
            <div>
              <SafeIcon icon={FiClock} className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Quick Turnaround</h3>
              <p>Most items ready within 24-48 hours</p>
            </div>
            <div>
              <SafeIcon icon={FiCheck} className="w-12 h-12 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Quality Guarantee</h3>
              <p>100% satisfaction or we'll redo it for free</p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl mb-8">Schedule your first pickup today and experience the difference</p>
            <Link
              to="/contact"
              className="bg-white text-blue-600 px-8 py-3 rounded-full text-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Schedule Now
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;